import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { dsWikiService } from '../services';
import type { ArticleCategory, ArticleType, Department, CustomFieldDefinition } from '../services/types';
import { PageSkeleton } from '@/components/base/Skeleton';

interface DraftArticle {
  title: string;
  subtitle: string;
  summary: string;
  content: string;
  topicId: string;
  categoryIds: string[];
  departmentId: string;
  articleTypeId: number | null;
  tags: string[];
  status: 'draft' | 'under_curation' | 'published';
  wordCount: number;
  estimatedReadingTime: number;
  customFieldResponses: Record<string, any>;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
}

type EditorMode = 'edit' | 'preview';

export default function ArticleCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeId = searchParams.get('type');

  const [editorMode, setEditorMode] = useState<EditorMode>('edit');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [article, setArticle] = useState<DraftArticle>({
    title: '',
    subtitle: '',
    summary: '',
    content: '',
    topicId: '',
    categoryIds: [],
    departmentId: '',
    articleTypeId: typeId ? parseInt(typeId) : null,
    tags: [],
    status: 'draft',
    wordCount: 0,
    estimatedReadingTime: 0,
    customFieldResponses: {}
  });

  // API data states
  const [apiCategories, setApiCategories] = useState<ArticleCategory[]>([]);
  const [apiDepartments, setApiDepartments] = useState<Department[]>([]);
  const [articleType, setArticleType] = useState<ArticleType | null>(null);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch article type details and master data
  useEffect(() => {
    const fetchData = async () => {
      setApiLoading(true);
      setApiError(null);
      try {
        const [categoriesRes, departmentsRes] = await Promise.all([
          dsWikiService.getActiveArticleCategories(),
          dsWikiService.getActiveDepartments()
        ]);
        setApiCategories(categoriesRes.data || []);
        setApiDepartments(departmentsRes.data || []);

        // Fetch article type if typeId is provided
        if (typeId) {
          const articleTypeRes = await dsWikiService.getArticleTypeById(parseInt(typeId));
          if (articleTypeRes.data) {
            setArticleType(articleTypeRes.data);
            // Parse custom fields from the article type
            const fields = articleTypeRes.data.cstm_flds_jn || [];
            setCustomFields(Array.isArray(fields) ? fields : []);
          }
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setApiError(err.message || 'Failed to fetch data');
      } finally {
        setApiLoading(false);
      }
    };

    fetchData();
  }, [typeId]);

  // Transform API data to UI format
  const topics = apiCategories.map(cat => ({
    id: String(cat.dswiki_ctgry_id),
    name: cat.ctgry_nm
  }));

  const departments = apiDepartments.map(dept => ({
    id: String(dept.dswiki_dpt_id),
    name: dept.dpt_nm
  }));

  // Auto-save functionality
  useEffect(() => {
    const autoSave = setTimeout(() => {
      if (article.title || article.content) {
        // handleSaveDraft();
      }
    }, 30000);

    return () => clearTimeout(autoSave);
  }, [article]);

  // Calculate word count and reading time
  useEffect(() => {
    const text = article.content.replace(/<[^>]*>/g, '');
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
    const estimatedReadingTime = Math.ceil(wordCount / 200);

    setArticle(prev => ({
      ...prev,
      wordCount,
      estimatedReadingTime
    }));
  }, [article.content]);

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const articleData = {
        artcl_nm: article.title,
        artcl_smry_tx: article.summary,
        artcl_cntnt_tx: article.content,
        dswiki_artcl_ctgry_id: article.topicId ? parseInt(article.topicId) : null,
        dswiki_dpt_id: article.departmentId ? parseInt(article.departmentId) : null,
        dswiki_artcl_type_id: article.articleTypeId,
        cstm_flds_rspns_jn: article.customFieldResponses,
        dswiki_artcl_sts_id: 1 // Draft status
      };

      const response = await dsWikiService.saveArticle(articleData);
      if (response.data) {
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForCuration = async () => {
    if (!article.title || !article.content) {
      alert('Please fill in all required fields before submitting.');
      return;
    }

    // Validate required custom fields
    const missingRequiredFields = customFields
      .filter(field => field.is_required && !article.customFieldResponses[field.field_id])
      .map(field => field.field_name);

    if (missingRequiredFields.length > 0) {
      alert(`Please fill in required fields: ${missingRequiredFields.join(', ')}`);
      return;
    }

    try {
      setIsSaving(true);
      const articleData = {
        artcl_nm: article.title,
        artcl_smry_tx: article.summary,
        artcl_cntnt_tx: article.content,
        dswiki_artcl_ctgry_id: article.topicId ? parseInt(article.topicId) : null,
        dswiki_dpt_id: article.departmentId ? parseInt(article.departmentId) : null,
        dswiki_artcl_type_id: article.articleTypeId,
        cstm_flds_rspns_jn: article.customFieldResponses,
        dswiki_artcl_sts_id: 2 // Under curation status
      };

      const response = await dsWikiService.saveArticle(articleData);
      if (response.data) {
        navigate('/app/ds-wiki/articles');
      }
    } catch (error) {
      console.error('Error submitting article:', error);
      alert('Error submitting article. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof DraftArticle, value: any) => {
    setArticle(prev => ({ ...prev, [field]: value }));
  };

  const updateCustomField = (fieldId: string, value: any) => {
    setArticle(prev => ({
      ...prev,
      customFieldResponses: {
        ...prev.customFieldResponses,
        [fieldId]: value
      }
    }));
  };

  const addTag = (tag: string) => {
    if (tag && !article.tags.includes(tag)) {
      setArticle(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tag: string) => {
    setArticle(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // File upload handlers
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    Array.from(files).forEach(file => {
      const newFile: UploadedFile = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: 'uploading'
      };

      setUploadedFiles(prev => [...prev, newFile]);

      // Simulate upload progress (replace with actual upload logic)
      simulateUpload(newFile.id);
    });
  };

  const simulateUpload = (fileId: string) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, progress: Math.min(progress, 100), status: progress >= 100 ? 'completed' : 'uploading' }
            : f
        )
      );
      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 200);
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render custom field based on field type
  const renderCustomField = (field: CustomFieldDefinition) => {
    const value = article.customFieldResponses[field.field_id] || '';

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateCustomField(field.field_id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.field_name.toLowerCase()}`}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => updateCustomField(field.field_id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.field_name.toLowerCase()}`}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => updateCustomField(field.field_id, e.target.value)}
            placeholder={field.placeholder || '0'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => updateCustomField(field.field_id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => updateCustomField(field.field_id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select {field.field_name}</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => updateCustomField(field.field_id, e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700">{field.placeholder || 'Yes'}</span>
          </label>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateCustomField(field.field_id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
    }
  };

  if (apiLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumbs */}
        <div className="flex items-center text-sm text-gray-500 mb-6">
          <Link to="/app/ds-wiki" className="hover:text-blue-600">Wiki</Link>
          <i className="ri-arrow-right-s-line mx-2"></i>
          <Link to="/app/ds-wiki/articles" className="hover:text-blue-600">Articles</Link>
          <i className="ri-arrow-right-s-line mx-2"></i>
          <span className="text-gray-900">Create {articleType?.artcl_type_nm || 'Article'}</span>
        </div>

        {/* Article Type Banner */}
        {articleType && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-file-text-line text-2xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold">{articleType.artcl_type_nm}</h1>
                {articleType.dscn_tx && (
                  <p className="text-blue-100 text-sm">{articleType.dscn_tx}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar - Status & Actions */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Article Authoring
              </h2>

              {/* Status */}
              <div className="bg-gray-100 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {article.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="text-sm space-y-1 text-gray-600">
                  <div className="flex justify-between">
                    <span>Words:</span>
                    <span className="font-medium">{article.wordCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Read time:</span>
                    <span className="font-medium">{article.estimatedReadingTime} min</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Files:</span>
                    <span className="font-medium">{uploadedFiles.length}</span>
                  </div>
                </div>
                {lastSaved && (
                  <div className="text-xs text-gray-500 mt-2">
                    Saved {lastSaved.toLocaleTimeString()}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Saving...
                    </div>
                  ) : (
                    <>
                      <i className="ri-save-line mr-2"></i>
                      Save Draft
                    </>
                  )}
                </button>

                <button
                  onClick={handleSubmitForCuration}
                  disabled={isSaving || !article.title || !article.content}
                  className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  Submit for Review
                </button>

                <Link
                  to="/app/ds-wiki/articles"
                  className="block w-full px-3 py-2 text-sm text-center border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <i className="ri-close-line mr-2"></i>
                  Cancel
                </Link>
              </div>

              {/* Required Fields Info */}
              {customFields.filter(f => f.is_required).length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Required fields for this type:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {customFields.filter(f => f.is_required).map(field => (
                      <li key={field.field_id} className="flex items-center gap-1">
                        <i className={`ri-checkbox-${article.customFieldResponses[field.field_id] ? 'circle' : 'blank-circle'}-line text-${article.customFieldResponses[field.field_id] ? 'green' : 'gray'}-500`}></i>
                        {field.field_name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Main Content - Editor */}
          <div className="col-span-9">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {/* Editor Mode Tabs */}
              <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setEditorMode('edit')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      editorMode === 'edit'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Edit
                  </button>
                  <button
                    onClick={() => setEditorMode('preview')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      editorMode === 'preview'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-eye-line mr-2"></i>
                    Preview
                  </button>
                </div>

                <div className="text-sm text-gray-500">
                  {article.wordCount} words | {article.estimatedReadingTime} min read
                </div>
              </div>

              {editorMode === 'edit' ? (
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={article.title}
                      onChange={(e) => updateField('title', e.target.value)}
                      placeholder="Enter article title"
                      className="w-full px-4 py-3 text-xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Custom Fields Section - Article Type Specific */}
                  {customFields.length > 0 && (
                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                      <h3 className="text-sm font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <i className="ri-list-settings-line"></i>
                        {articleType?.artcl_type_nm} Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {customFields.map((field) => (
                          <div key={field.field_id} className={field.field_type === 'textarea' ? 'col-span-2' : ''}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {field.field_name}
                              {field.is_required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {renderCustomField(field)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Department & Category Row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Department */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Department
                      </label>
                      <select
                        value={article.departmentId}
                        onChange={(e) => updateField('departmentId', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={article.topicId}
                        onChange={(e) => updateField('topicId', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select category</option>
                        {topics.map(topic => (
                          <option key={topic.id} value={topic.id}>{topic.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Summary */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Summary
                    </label>
                    <textarea
                      value={article.summary}
                      onChange={(e) => updateField('summary', e.target.value)}
                      placeholder="Write a brief summary of your article"
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {article.tags.map(tag => (
                        <span
                          key={tag}
                          className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {tag}
                          <button
                            onClick={() => removeTag(tag)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="Add tags (press Enter)"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag((e.target as HTMLInputElement).value.trim());
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Content Editor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={article.content}
                      onChange={(e) => updateField('content', e.target.value)}
                      placeholder="Write your article content here (supports HTML)"
                      rows={15}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>

                  {/* File Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-attachment-2 mr-1"></i>
                      Attachments
                    </label>

                    {/* Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                        isDragging
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => handleFileSelect(e.target.files)}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                      />
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <i className="ri-upload-cloud-2-line text-2xl text-gray-400"></i>
                        </div>
                        <p className="text-gray-600">
                          Drag & drop files here, or{' '}
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-gray-400">
                          Supports PDF, Word, Excel, PowerPoint, Images, ZIP (max 25MB each)
                        </p>
                      </div>
                    </div>

                    {/* Uploaded Files List */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {uploadedFiles.map(file => (
                          <div
                            key={file.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                file.type.includes('pdf') ? 'bg-red-100 text-red-600' :
                                file.type.includes('word') || file.type.includes('document') ? 'bg-blue-100 text-blue-600' :
                                file.type.includes('sheet') || file.type.includes('excel') ? 'bg-green-100 text-green-600' :
                                file.type.includes('image') ? 'bg-purple-100 text-purple-600' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                <i className={`text-lg ${
                                  file.type.includes('pdf') ? 'ri-file-pdf-line' :
                                  file.type.includes('word') || file.type.includes('document') ? 'ri-file-word-line' :
                                  file.type.includes('sheet') || file.type.includes('excel') ? 'ri-file-excel-line' :
                                  file.type.includes('image') ? 'ri-image-line' :
                                  'ri-file-line'
                                }`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                              </div>
                            </div>

                            {file.status === 'uploading' ? (
                              <div className="flex items-center gap-3">
                                <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-500 transition-all duration-200"
                                    style={{ width: `${file.progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-500 w-8">{file.progress}%</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <i className="ri-check-line text-green-500"></i>
                                <button
                                  onClick={() => removeFile(file.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Preview Mode */
                <div className="prose prose-lg max-w-none">
                  <h1>{article.title || 'Untitled Article'}</h1>
                  {article.subtitle && <p className="lead">{article.subtitle}</p>}

                  {/* Custom Fields Preview */}
                  {Object.keys(article.customFieldResponses).length > 0 && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6 not-prose">
                      <h3 className="text-sm font-semibold text-blue-900 mb-3">{articleType?.artcl_type_nm} Details</h3>
                      <dl className="grid grid-cols-2 gap-2 text-sm">
                        {customFields.map(field => {
                          const value = article.customFieldResponses[field.field_id];
                          if (!value) return null;
                          return (
                            <div key={field.field_id} className="flex justify-between">
                              <dt className="text-gray-600">{field.field_name}:</dt>
                              <dd className="font-medium text-gray-900">{String(value)}</dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  )}

                  {article.summary && (
                    <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                      <p className="font-semibold mb-2">Summary</p>
                      <p>{article.summary}</p>
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: article.content || '<p>No content yet</p>' }} />

                  {/* Attachments Preview */}
                  {uploadedFiles.length > 0 && (
                    <div className="border-t border-gray-200 pt-4 mt-6 not-prose">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Attachments ({uploadedFiles.length})</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {uploadedFiles.map(file => (
                          <div key={file.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <i className="ri-attachment-2 text-gray-400"></i>
                            <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
